import { Capacitor } from '@capacitor/core'
import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'
import * as Yup from 'yup'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import {
  isNoMedicationEntry,
  type LegacyMedicationStatement,
} from '@/@bothrs-ui/web/components/forms/medication/legacy-medication.helpers'
import { FormikTextArea } from '@/@bothrs-ui/web/components/forms/text-area/FormikTextArea'
import { Flex, FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { breakpoints } from '@/config/layout'

// ================================ LegacyMedicationForm ================================

type LegacyMedicationFormProps = {
  medications: LegacyMedicationStatement[]
  onAdd: (medication: LegacyMedicationStatement) => void
  onEdit: (index: number, medication: LegacyMedicationStatement) => void
  onRemove: (index: number) => void
}

export const LegacyMedicationForm = ({ medications, onAdd, onEdit, onRemove }: LegacyMedicationFormProps) => {
  // State
  // ------------------------------------------------------------------------- /
  const [showDialog, setShowDialog] = useState(false)
  const [medicationToEdit, setMedicationToEdit] = useState<LegacyMedicationStatement | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const isMobile = useMediaQuery(breakpoints.toS) || Capacitor.isNativePlatform()

  // Interaction handlers
  // ------------------------------------------------------------------------- /
  const handleSubmit = (medication: LegacyMedicationStatement) => {
    if (editingIndex === null) {
      onAdd(medication)
    } else {
      onEdit(editingIndex, medication)
    }
    setShowDialog(false)
    setMedicationToEdit(null)
    setEditingIndex(null)
  }

  const handleEditMedication = (medicationIndex: number) => {
    const medication = medications[medicationIndex]
    if (!medication) {
      return
    }
    setMedicationToEdit(medication)
    setEditingIndex(medicationIndex)
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setMedicationToEdit(null)
    setEditingIndex(null)
  }

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <FlexColumn gap={theme.UI.Spacing.Space6}>
      {medications.length > 0 && (
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          {medications.map((medication, index) => {
            if (isNoMedicationEntry(medication)) {
              return (
                <StMedicationItem key='no-medication' type='BodyMediumRegular'>
                  {t({
                    id: 'medication.noMedication.label',
                    message: 'Je neemt geen thuismedicatie',
                  })}
                </StMedicationItem>
              )
            }

            return (
              <StMedicationItem key={`${medication.name}-${index}`} type='BodyMediumRegular'>
                <Flex flexDirection='row' justifyContent='space-between' alignItems='center'>
                  <FlexColumn gap={theme.UI.Spacing.Space1}>
                    <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darker}>
                      {medication.name}
                    </Span>
                    <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                      {medication.frequency} - {medication.intake}
                    </Span>
                  </FlexColumn>
                  <FlexRow gap={theme.UI.Spacing.Space3}>
                    <IconButton
                      type='button'
                      size='large'
                      icon={['far', 'pen']}
                      variant='Tertiary'
                      onClick={() => handleEditMedication(index)}
                    />
                    <IconButton
                      size='large'
                      type='button'
                      icon={['fal', 'trash-can']}
                      variant='Tertiary'
                      color={'#DA3E33'}
                      onClick={() => onRemove(index)}
                    />
                  </FlexRow>
                </Flex>
              </StMedicationItem>
            )
          })}
        </FlexColumn>
      )}

      <Button
        variant='Secondary'
        type='button'
        onClick={() => {
          setMedicationToEdit(null)
          setEditingIndex(null)
          setShowDialog(true)
        }}
        prefixIcon={['fal', 'plus']}
      >
        {t({ id: 'medication.form.addButton', message: 'Medicatie toevoegen' })}
      </Button>

      {isMobile ? (
        <BottomSheet steps={[85, 100]} visible={showDialog} onClose={handleCloseDialog}>
          <StBottomSheetContent>
            <SingleMedicationForm onSubmit={handleSubmit} medicationToEdit={medicationToEdit} />
          </StBottomSheetContent>
        </BottomSheet>
      ) : (
        <Modal isVisible={showDialog} onClose={handleCloseDialog} maxHeight='90vh'>
          <StModalContent>
            <SingleMedicationForm onSubmit={handleSubmit} medicationToEdit={medicationToEdit} />
          </StModalContent>
        </Modal>
      )}
    </FlexColumn>
  )
}

// ================================ SingleMedicationForm ================================

type SingleMedicationFormProps = {
  onSubmit: (medication: LegacyMedicationStatement) => void
  medicationToEdit?: LegacyMedicationStatement | null
}

/**
 * Soft medication name & dose validation.
 *
 * Rules:
 *  1. Minimum 6 characters
 *  2. At least one word (2+ letters)
 *  3. At least one digit
 */
function hasWord(input: string): boolean {
  return /[A-Za-zÀ-ÿ]{2,}/.test(input)
}

function hasDigit(input: string): boolean {
  return /\d/.test(input)
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required(
      t({
        id: 'medication.form.name.required',
        message: 'Naam en dosis zijn verplicht',
      }),
    )
    .max(
      100,
      t({
        id: 'medication.form.name.maxLength',
        message: 'Naam en dosis mag maximaal 100 tekens bevatten',
      }),
    )
    .test('medication-name-dose', '', function (value) {
      const trimmed = value?.trim() ?? ''
      const wordPresent = hasWord(trimmed)
      const digitPresent = hasDigit(trimmed)

      if (trimmed.length < 6 || !wordPresent || !digitPresent) {
        let errorMessage: string

        if (wordPresent && !digitPresent) {
          errorMessage = t({
            id: 'medication.form.name.missingDose',
            message: 'Voeg een dosering toe, bv. 1g, 500mg, 1%',
          })
        } else if (digitPresent && !wordPresent) {
          errorMessage = t({
            id: 'medication.form.name.missingName',
            message: 'Voeg de naam van het medicijn toe',
          })
        } else {
          errorMessage = t({
            id: 'medication.form.name.invalidFormat',
            message: 'Vul een naam en dosis in, bv. Dafalgan Instant Forte 1g, Cremicort 1%',
          })
        }

        return this.createError({ message: errorMessage })
      }

      return true
    }),
  frequency: Yup.string()
    .required(
      t({
        id: 'medication.form.frequency.required',
        message: 'Frequentie is verplicht',
      }),
    )
    .max(
      50,
      t({
        id: 'medication.form.frequency.maxLength',
        message: 'Frequentie mag maximaal 50 tekens bevatten',
      }),
    ),
  intake: Yup.string()
    .required(
      t({
        id: 'medication.form.intake.required',
        message: 'Moment van inname is verplicht',
      }),
    )
    .max(
      200,
      t({
        id: 'medication.form.intake.maxLength',
        message: 'Moment van inname mag maximaal 200 tekens bevatten',
      }),
    ),
})

const SingleMedicationForm = ({ onSubmit, medicationToEdit }: SingleMedicationFormProps) => {
  const theme = useTheme()

  const initialValues = medicationToEdit ?? {
    name: '',
    frequency: '',
    intake: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { resetForm }) => {
        onSubmit(values)
        resetForm()
      }}
    >
      {({ isValid, isSubmitting }) => (
        <Form style={{ height: '100%' }}>
          <Flex flexDirection='column' justifyContent='space-between' style={{ minHeight: '50vh', height: '100%' }}>
            <FlexColumn
              gap={theme.UI.Spacing.Space6}
              style={{
                overflowY: 'auto',
                paddingBottom: theme.UI.SpacingPx.Space6,
              }}
            >
              <Input
                name='name'
                type='text'
                data-1p-ignore
                autoComplete='off'
                placeholder={t({
                  id: 'medication.form.name.placeholder',
                  message: 'bv. Dafalgan Instant Forte 1g',
                })}
                label={t({
                  id: 'medication.form.name.label',
                  message: 'Naam & dosis',
                })}
                hint={t({
                  id: 'medication.form.name.hint',
                  message: 'Vul een naam en dosis in, bv. "Dafalgan Instant Forte 1g", "Cremicort 1%"',
                })}
                useDefaultLabel
                noMargin
                style={{ fontSize: 16, fontFamily: 'DINOT' }}
              />
              <Input
                name='frequency'
                type='text'
                data-1p-ignore
                autoComplete='off'
                placeholder={t({
                  id: 'medication.form.frequency.placeholder',
                  message: 'bv. 2x per dag',
                })}
                label={t({
                  id: 'medication.form.frequency.label',
                  message: 'Frequentie',
                })}
                useDefaultLabel
                noMargin
                style={{ fontSize: 16, fontFamily: 'DINOT' }}
              />
              <FormikTextArea
                name='intake'
                rows={2}
                data-1p-ignore
                autoComplete='off'
                placeholder={t({
                  id: 'medication.form.intake.placeholder',
                  message: 'bv. bij het ontbijt, middagmaal, avondeten of slapengaan',
                })}
                label={t({
                  id: 'medication.form.intake.label',
                  message: 'Moment van inname',
                })}
                noMargin
                style={{ fontSize: 16, fontFamily: 'DINOT' }}
              />
            </FlexColumn>
            <Button type='submit' variant='Primary' disabled={isSubmitting || !isValid}>
              {medicationToEdit
                ? t({
                    id: 'medication.form.saveButton',
                    message: 'Opslaan',
                  })
                : t({
                    id: 'medication.form.addButton',
                    message: 'Medicatie toevoegen',
                  })}
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  )
}

// ================================ Styled Components ================================

const StMedicationItem = styled(Span)`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space4};
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
`

const StModalContent = styled.div`
  display: flex;
  flex-direction: column;
`

const StBottomSheetContent = styled.div`
  padding: ${({ theme }) => `0 ${theme.UI.SpacingPx.Space4} ${theme.UI.SpacingPx.Space6}`};
  height: 100%;
`
